// Bloco de campos de endereço de cobrança pra formulários de cartão.
// Compartilhado entre student-new (FormaPagamentoView + CompraPacoteModal)
// e pay-new (CardBlock). Centraliza pra que mudanças (validação, máscara,
// ViaCEP lookup) virem em um único lugar.
//
// Por que um helper só de "fields" e não um Form completo? Porque cada
// caller tem seu próprio submit/erro/estado. Isso aqui só renderiza inputs
// controlados; o pai dono dos values + setters.
//
// CEP autofill: quando o usuário digita um CEP válido (8 dígitos), chamamos
// o ViaCEP (API pública, sem auth) e pré-preenchemos logradouro/bairro/
// cidade/UF. Os campos ficam editáveis — depois do autofill, o usuário pode
// corrigir qualquer campo (ex.: número da casa, complemento). Re-buscar
// só dispara quando o CEP muda DE NOVO (não a cada keystroke).

import { useEffect, useRef, useState } from "preact/hooks";
import { Loader2 } from "lucide-react";

export interface BillingAddress {
  zip: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

export const emptyBillingAddress: BillingAddress = {
  zip: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: ""
};

/** Validação client-side dos campos obrigatórios. Retorna mensagem ou null. */
export function validateBillingAddress(addr: BillingAddress): string | null {
  const zip = addr.zip.replace(/\D/g, "");
  if (zip.length !== 8) return "CEP deve ter 8 dígitos.";
  if (!addr.street.trim()) return "Logradouro é obrigatório.";
  if (!addr.number.trim()) return "Número é obrigatório.";
  if (!addr.city.trim()) return "Cidade é obrigatória.";
  if (addr.state.trim().length !== 2) return "Estado (UF) deve ter 2 letras.";
  return null;
}

interface ViaCepResponse {
  cep?: string;
  logradouro?: string;
  complemento?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean;
}

interface Props {
  value: BillingAddress;
  onChange: (next: BillingAddress) => void;
  disabled?: boolean;
}

export function BillingAddressFields({ value, onChange, disabled }: Props) {
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  // Lembra o último CEP buscado pra não disparar fetch repetido a cada
  // re-render quando o pai recria o `value` mas o CEP não mudou.
  const lastLookupRef = useRef<string>("");

  function set<K extends keyof BillingAddress>(key: K, v: BillingAddress[K]) {
    onChange({ ...value, [key]: v });
  }

  // Quando o CEP completa 8 dígitos, busca no ViaCEP. Substitui os campos
  // sempre (autofill prevalece sobre o que estava preenchido) — usuário pode
  // editar depois. Race-condition handled pelo lastLookupRef + AbortController.
  useEffect(() => {
    const zip = value.zip.replace(/\D/g, "");
    if (zip.length !== 8) {
      setLookupError(null);
      return;
    }
    if (lastLookupRef.current === zip) return;
    lastLookupRef.current = zip;

    const ac = new AbortController();
    setLookingUp(true);
    setLookupError(null);
    fetch(`https://viacep.com.br/ws/${zip}/json/`, { signal: ac.signal })
      .then((r) => r.json() as Promise<ViaCepResponse>)
      .then((data) => {
        if (ac.signal.aborted) return;
        if (data.erro) {
          setLookupError("CEP não encontrado.");
          return;
        }
        onChange({
          ...value,
          zip,
          street: data.logradouro ?? value.street,
          neighborhood: data.bairro ?? value.neighborhood,
          city: data.localidade ?? value.city,
          state: (data.uf ?? value.state).toUpperCase(),
          // ViaCEP às vezes retorna complemento útil (ex: "lado ímpar") —
          // só usa se o caller não tiver digitado nada manualmente.
          complement:
            value.complement || (data.complemento ?? "")
        });
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        setLookupError(err instanceof Error ? err.message : "Falha no CEP.");
      })
      .finally(() => {
        if (!ac.signal.aborted) setLookingUp(false);
      });
    return () => {
      ac.abort();
    };
    // value/onChange não entram nas deps de propósito — só queremos disparar
    // quando o CEP digitado muda, não em qualquer mudança no value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.zip]);

  return (
    <div class="space-y-2">
      <div class="grid grid-cols-3 gap-2">
        <div class="col-span-1">
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            CEP
          </label>
          <div class="relative">
            <input
              type="text"
              inputMode="numeric"
              required
              value={value.zip}
              onInput={(e) => set("zip", (e.target as HTMLInputElement).value)}
              placeholder="00000-000"
              class="w-full rounded-md border border-border bg-background px-3 py-2 pr-8 text-sm"
              disabled={disabled}
            />
            {lookingUp && (
              <Loader2 className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
          {lookupError && (
            <p class="mt-1 text-[10px] text-amber-600">{lookupError}</p>
          )}
        </div>
        <div class="col-span-2">
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Logradouro
          </label>
          <input
            type="text"
            required
            value={value.street}
            onInput={(e) => set("street", (e.target as HTMLInputElement).value)}
            placeholder="Rua / Avenida"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={disabled}
          />
        </div>
      </div>
      <div class="grid grid-cols-3 gap-2">
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Número
          </label>
          <input
            type="text"
            required
            value={value.number}
            onInput={(e) => set("number", (e.target as HTMLInputElement).value)}
            placeholder="123"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={disabled}
          />
        </div>
        <div class="col-span-2">
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Complemento (opcional)
          </label>
          <input
            type="text"
            value={value.complement}
            onInput={(e) =>
              set("complement", (e.target as HTMLInputElement).value)
            }
            placeholder="Apto 12, bloco B…"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={disabled}
          />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Bairro (opcional)
        </label>
        <input
          type="text"
          value={value.neighborhood}
          onInput={(e) =>
            set("neighborhood", (e.target as HTMLInputElement).value)
          }
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          disabled={disabled}
        />
      </div>
      <div class="grid grid-cols-3 gap-2">
        <div class="col-span-2">
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Cidade
          </label>
          <input
            type="text"
            required
            value={value.city}
            onInput={(e) => set("city", (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={disabled}
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            UF
          </label>
          <input
            type="text"
            required
            maxLength={2}
            value={value.state}
            onInput={(e) =>
              set("state", (e.target as HTMLInputElement).value.toUpperCase())
            }
            placeholder="SP"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
